import { createContext, useContext, useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LAYOUT_KEY, confirmsLayout, legacyLayoutToAdopt, patchProfileLayout } from "@/features/navigation/homeLayout";
import { profileService, type UserProfile } from "@/services/profile.service";
import { notificationsService } from "@/services/notifications.service";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { App as CapacitorApp } from "@capacitor/app";
import { syncPushEnabledState, checkPushPermissionGranted, requestPushPermissionsAndRegister } from "@/utils/push-notifications";

// Types
export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";
export type MapLayout = "categories" | "locations" | "stashes";


export interface Preferences {
  theme: ThemePreference;
  showSensitiveContent: boolean;
  notificationsEnabled: boolean;
  showTitlesOnThumbs: boolean;
  showYouMarker: boolean;
  mapLayout: MapLayout;
}

interface PreferencesContextType {
  preferences: Preferences;
  resolvedTheme: ResolvedTheme;
  isLoading: boolean;

  setTheme: (theme: ThemePreference) => Promise<void>;
  setShowSensitiveContent: (value: boolean) => Promise<void>;
  setNotificationsEnabled: (value: boolean) => Promise<void>;
  setShowTitlesOnThumbs: (value: boolean) => Promise<void>;
  setShowYouMarker: (value: boolean) => void;
  setMapLayout: (layout: MapLayout) => void;

  toggleTheme: () => Promise<void>;
  forceTheme: (theme: ResolvedTheme) => void;
  resetForcedTheme: () => void;
}

// localStorage keys
const STORAGE_KEYS = {
  THEME: "stasht_theme",
  SHOW_SENSITIVE: "stasht_show_sensitive",
  NOTIFICATIONS: "stasht_notifications",
  SHOW_TITLES: "stasht_show_titles_on_thumbs",
  SHOW_YOU_MARKER: "stasht_show_you_marker",
  MAP_LAYOUT: "stasht_map_layout",
} as const;

// Defaults
const DEFAULTS: Preferences = {
  theme: "system",
  showSensitiveContent: false,
  notificationsEnabled: true,
  showTitlesOnThumbs: true,
  showYouMarker: true,
  mapLayout: "stashes" as MapLayout,
};

const buildNotificationChannels = (enabled: boolean) => ({
  push: enabled,
  in_app: enabled,
  email: enabled,
  sms: false,
});

// localStorage helpers
const readString = (key: string, fallback: string): string => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

const readBoolean = (key: string, fallback: boolean): boolean => {
  try {
    const val = localStorage.getItem(key);
    if (val === null) return fallback;
    return val === "true";
  } catch {
    return fallback;
  }
};

const writeString = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore
  }
};

const writeBoolean = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Ignore
  }
};

const removeKey = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore
  }
};

const readAllFromStorage = (): Preferences => ({
  theme: readString(STORAGE_KEYS.THEME, DEFAULTS.theme) as ThemePreference,
  showSensitiveContent: readBoolean(STORAGE_KEYS.SHOW_SENSITIVE, DEFAULTS.showSensitiveContent),
  notificationsEnabled: readBoolean(STORAGE_KEYS.NOTIFICATIONS, DEFAULTS.notificationsEnabled),
  showTitlesOnThumbs: readBoolean(STORAGE_KEYS.SHOW_TITLES, DEFAULTS.showTitlesOnThumbs),
  showYouMarker: readBoolean(STORAGE_KEYS.SHOW_YOU_MARKER, DEFAULTS.showYouMarker),
  mapLayout: readString(STORAGE_KEYS.MAP_LAYOUT, DEFAULTS.mapLayout) as MapLayout,
});

const writeAllToStorage = (prefs: Preferences) => {
  writeString(STORAGE_KEYS.THEME, prefs.theme);
  writeBoolean(STORAGE_KEYS.SHOW_SENSITIVE, prefs.showSensitiveContent);
  writeBoolean(STORAGE_KEYS.NOTIFICATIONS, prefs.notificationsEnabled);
  writeBoolean(STORAGE_KEYS.SHOW_TITLES, prefs.showTitlesOnThumbs);
  writeBoolean(STORAGE_KEYS.SHOW_YOU_MARKER, prefs.showYouMarker);
  writeString(STORAGE_KEYS.MAP_LAYOUT, prefs.mapLayout);
};

const clearUserPreferencesFromStorage = () => {
  // Clear all except theme (theme persists for logged-out experience)
  removeKey(STORAGE_KEYS.SHOW_SENSITIVE);
  removeKey(STORAGE_KEYS.NOTIFICATIONS);
  removeKey(STORAGE_KEYS.SHOW_TITLES);
  removeKey(STORAGE_KEYS.SHOW_YOU_MARKER);
  removeKey(STORAGE_KEYS.MAP_LAYOUT);
  removeKey(LAYOUT_KEY);
};

// Context
const PreferencesContext = createContext<PreferencesContextType | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();

  const [preferences, setPreferences] = useState<Preferences>(readAllFromStorage);
  const [isLoading, setIsLoading] = useState(isAuthenticated);
  const [forcedTheme, setForcedTheme] = useState<ResolvedTheme | null>(null);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  const activeUserIdRef = useRef<string | null>(null);
  useLayoutEffect(() => { activeUserIdRef.current = isAuthenticated ? user?.user_id ?? null : null; }, [isAuthenticated, user?.user_id]);
  const prevUserIdRef = useRef<string | null>(null);
  const initializedFromApiRef = useRef(false);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // Compute resolved theme
  const resolvedTheme = useMemo<ResolvedTheme>(() => {
    if (forcedTheme) return forcedTheme;
    if (preferences.theme === "system") return systemTheme;
    return preferences.theme;
  }, [forcedTheme, preferences.theme, systemTheme]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  // Fetch preferences from API
  const fetchFromApi = useCallback(async (owner: string) => {
    try {
      setIsLoading(true);

      // A fresh matching profile must win over a legacy value on this device.
      // fetchQuery still shares an in-flight useProfile request, but never adopts
      // from a restored cache whose layout field may predate server support.
      await queryClient.cancelQueries({ queryKey: ["profile"], exact: true });
      if (activeUserIdRef.current !== owner) return;
      const profile = await queryClient.fetchQuery({
        queryKey: ["profile"],
        queryFn: () => profileService.getProfile(),
        staleTime: 0,
      });
      if (activeUserIdRef.current !== owner || profile.user_id !== owner) return;
      let legacy: string | null = null;
      try { legacy = localStorage.getItem(LAYOUT_KEY); } catch { /* Storage unavailable. */ }
      const adoption = legacyLayoutToAdopt(profile, owner, legacy);
      if (adoption) {
        try {
          const updated = await profileService.updateProfile({ home_layout: adoption });
          if (activeUserIdRef.current !== owner) return;
          if (!confirmsLayout(updated, owner, adoption)) throw new Error("Server did not confirm the layout");
          queryClient.setQueryData<UserProfile>(["profile"], current => patchProfileLayout(current, owner, adoption));
          removeKey(LAYOUT_KEY);
        } catch (error) {
          if (activeUserIdRef.current !== owner) return;
          toast.error("Failed to save preference");
          console.warn("Failed to adopt Home layout:", error);
        }
      } else if (profile.home_layout != null) {
        // This account already has a cloud layout; the obsolete device value loses.
        removeKey(LAYOUT_KEY);
      }
      if (activeUserIdRef.current !== owner) return;
      const settings = profile?.settings as Record<string, unknown> | undefined;

      if (settings) {
        const apiPrefs: Partial<Preferences> = {
          showSensitiveContent: settings.show_sensitive_content === true,
          notificationsEnabled: settings.notifications_enabled !== false,
          showTitlesOnThumbs: settings.show_titles_on_thumbs !== false,
        };

        // Only sync theme from API if it's explicitly set (not 'system')
        if (settings.theme_preference === "light" || settings.theme_preference === "dark") {
          apiPrefs.theme = settings.theme_preference;
        }

        setPreferences(prev => {
          const merged = { ...prev, ...apiPrefs };
          writeAllToStorage(merged);
          return merged;
        });
      }
    } catch (error) {
      console.warn("Failed to fetch preferences from API:", error);
    } finally {
      if (activeUserIdRef.current === owner) setIsLoading(false);
    }
  }, [queryClient]);

  // Handle auth state changes
  useEffect(() => {
    const currentUserId = isAuthenticated ? user?.user_id ?? null : null;
    const prevUserId = prevUserIdRef.current;

    if (currentUserId !== prevUserId) {
      if (prevUserId !== null && currentUserId === null) {
        // Logout: clear user preferences, keep theme
        clearUserPreferencesFromStorage();
        setPreferences(prev => ({
          ...DEFAULTS,
          theme: prev.theme, // Keep theme for guest experience
        }));
        initializedFromApiRef.current = false;
        setIsLoading(false);
      } else if (currentUserId !== null) {
        // New user (login or impersonation)
        if (prevUserId !== null) {
          // Impersonation: clear previous user's prefs
          clearUserPreferencesFromStorage();
        }
        void fetchFromApi(currentUserId);
        initializedFromApiRef.current = true;
      }
      prevUserIdRef.current = currentUserId;
    } else if (isAuthenticated && !initializedFromApiRef.current) {
      // Initial mount while already authenticated
      if (currentUserId) void fetchFromApi(currentUserId);
      initializedFromApiRef.current = true;
    }
  }, [user?.user_id, isAuthenticated, fetchFromApi]);

  // Reconcile notifications toggle with actual OS permission state.
  // The DB flag can say "enabled" even when iOS never granted permissions.
  // OS is the source of truth — if not granted, force the flag to false.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isAuthenticated) return;

    const reconcile = async () => {
      if (!preferences.notificationsEnabled) return;
      const granted = await checkPushPermissionGranted();
      if (!granted) {
        setPreferences(prev => ({ ...prev, notificationsEnabled: false }));
        writeBoolean(STORAGE_KEYS.NOTIFICATIONS, false);
        // Sync to server so backend stops attempting pushes
        profileService.updateProfile({ notifications_enabled: false }).catch(() => {});
      }
    };

    reconcile();

    // Re-check when app resumes (user might change permissions in iOS Settings)
    const listener = CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) reconcile();
    });

    return () => { void listener.then(l => l.remove()); };
  }, [isAuthenticated, preferences.notificationsEnabled]);

  // Generic preference updater with optimistic update and rollback
  const updatePreference = useCallback(
    async <K extends keyof Preferences>(
      key: K,
      value: Preferences[K],
      apiField: string,
      storageKey: string,
    ) => {
      const owner = activeUserIdRef.current;
      const prevValue = preferences[key];

      // Optimistic update
      setPreferences(prev => ({ ...prev, [key]: value }));

      if (typeof value === "boolean") {
        writeBoolean(storageKey, value);
      } else {
        writeString(storageKey, value as string);
      }

      // Skip API for theme='system' or if not authenticated
      if (key === "theme" && value === "system") return;
      if (!isAuthenticated) return;

      try {
        const apiValue = key === "theme" ? value : value;
        const updated = await profileService.updateProfile({ [apiField]: apiValue });
        if (activeUserIdRef.current !== owner || updated.user_id !== owner) return;
        queryClient.setQueryData<UserProfile>(["profile"], current => current?.user_id === owner
          ? { ...current, ...updated, home_layout: current.home_layout } : current);
      } catch (error) {
        if (activeUserIdRef.current !== owner) return;
        // Rollback
        setPreferences(prev => ({ ...prev, [key]: prevValue }));
        if (typeof prevValue === "boolean") {
          writeBoolean(storageKey, prevValue);
        } else {
          writeString(storageKey, prevValue as string);
        }
        toast.error("Failed to save preference");
        console.error("Failed to update preference:", error);
      }
    },
    [preferences, isAuthenticated, queryClient],
  );

  // Individual setters
  const setTheme = useCallback(
    (theme: ThemePreference) => updatePreference("theme", theme, "theme_preference", STORAGE_KEYS.THEME),
    [updatePreference],
  );

  const setShowSensitiveContent = useCallback(
    (value: boolean) =>
      updatePreference("showSensitiveContent", value, "show_sensitive_content", STORAGE_KEYS.SHOW_SENSITIVE),
    [updatePreference],
  );

  const setNotificationsEnabled = useCallback(
    async (value: boolean) => {
      const owner = activeUserIdRef.current;
      const prevValue = preferences.notificationsEnabled;

      // When enabling on native, check actual iOS permission state first
      if (value && Capacitor.isNativePlatform()) {
        const { receive } = await PushNotifications.checkPermissions();

        if (receive === "denied") {
          // iOS won't show the permission dialog again — send user to Settings
          try {
            const { AppLauncher } = await import("@capacitor/app-launcher");
            await AppLauncher.openUrl({ url: "app-settings:" });
          } catch {
            toast.error("Open Settings to enable notifications");
          }
          return;
        }

        if (receive !== "granted") {
          // First time or "prompt" — request permission from iOS
          const result = await requestPushPermissionsAndRegister();
          if (result?.receive !== "granted") {
            // User denied the system dialog
            return;
          }
        }
      }

      setPreferences(prev => ({ ...prev, notificationsEnabled: value }));
      writeBoolean(STORAGE_KEYS.NOTIFICATIONS, value);

      if (!isAuthenticated) {
        return;
      }

      const channels = buildNotificationChannels(value);
      try {
        const [profile] = await Promise.all([
          profileService.updateProfile({ notifications_enabled: value }),
          notificationsService.updatePreferences({ channels }),
          syncPushEnabledState(value),
        ]);
        if (profile && activeUserIdRef.current === owner && profile.user_id === owner) {
          queryClient.setQueryData<UserProfile>(["profile"], current => current?.user_id === owner
            ? { ...current, ...profile, home_layout: current.home_layout } : current);
        }
      } catch (error) {
        if (activeUserIdRef.current !== owner) return;
        setPreferences(prev => ({ ...prev, notificationsEnabled: prevValue }));
        writeBoolean(STORAGE_KEYS.NOTIFICATIONS, prevValue);
        toast.error("Failed to save notification preference");
        console.error("Failed to update notifications preference:", error);
      }
    },
    [preferences.notificationsEnabled, isAuthenticated, queryClient],
  );

  const setShowTitlesOnThumbs = useCallback(
    (value: boolean) =>
      updatePreference("showTitlesOnThumbs", value, "show_titles_on_thumbs", STORAGE_KEYS.SHOW_TITLES),
    [updatePreference],
  );

  // Admin display aid: local-only until the profile API accepts this setting.
  const setShowYouMarker = useCallback(
    (value: boolean) => {
      setPreferences(prev => ({ ...prev, showYouMarker: value }));
      writeBoolean(STORAGE_KEYS.SHOW_YOU_MARKER, value);
    },
    [],
  );

  // Map layout is local-only (not synced to API)
  const setMapLayout = useCallback(
    (layout: MapLayout) => {
      setPreferences(prev => ({ ...prev, mapLayout: layout }));
      writeString(STORAGE_KEYS.MAP_LAYOUT, layout);
    },
    [],
  );

  // Toggle theme: system -> light -> dark -> system
  const toggleTheme = useCallback(async () => {
    const next: ThemePreference =
      preferences.theme === "system" ? "light" : preferences.theme === "light" ? "dark" : "system";
    await setTheme(next);
  }, [preferences.theme, setTheme]);

  // Force theme (for preview/temporary override)
  const forceTheme = useCallback((theme: ResolvedTheme) => {
    setForcedTheme(theme);
  }, []);

  const resetForcedTheme = useCallback(() => {
    setForcedTheme(null);
  }, []);

  const value = useMemo<PreferencesContextType>(
    () => ({
      preferences,
      resolvedTheme,
      isLoading,
      setTheme,
      setShowSensitiveContent,
      setNotificationsEnabled,
      setShowTitlesOnThumbs,
      setShowYouMarker,
      setMapLayout,

      toggleTheme,
      forceTheme,
      resetForcedTheme,
    }),
    [
      preferences,
      resolvedTheme,
      isLoading,
      setTheme,
      setShowSensitiveContent,
      setNotificationsEnabled,
      setShowTitlesOnThumbs,
      setShowYouMarker,
      setMapLayout,

      toggleTheme,
      forceTheme,
      resetForcedTheme,
    ],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextType {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return context;
}

// Convenience hook for theme only (common use case)
export function useTheme() {
  const { preferences, resolvedTheme, setTheme, toggleTheme, forceTheme, resetForcedTheme } = usePreferences();
  return {
    theme: preferences.theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    forceTheme,
    resetForcedTheme,
  };
}

// Sync getter for sensitive content (for use outside React)
export function getSensitiveContentSetting(): boolean {
  return readBoolean(STORAGE_KEYS.SHOW_SENSITIVE, DEFAULTS.showSensitiveContent);
}

// Sync getter for display preferences (for use outside React)
export function getDisplayPreference(key: "showTitlesOnThumbs"): boolean {
  const storageKey = {
    showTitlesOnThumbs: STORAGE_KEYS.SHOW_TITLES,
  }[key];
  return readBoolean(storageKey, DEFAULTS[key]);
}
